import { useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
  EmployeesEmployeeSelectorInput,
  FetchAllowancesWithIncidencesDocument,
  TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
  TimeoffPolicyEmployeesCollectionListDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import uniqBy from 'lodash/uniqBy'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'

const getTitle = (step: FormState['step']) => {
  switch (step) {
    case 'adjustment_loss':
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
    case 'select_employees':
      return i18n.t('settings.timeoff_policies.add_employees_modal.assign.title')
    case 'map_allowances':
      return i18n.t('settings.timeoff_policies.manual_policy_mapper.reassign')
  }
}

const getDescription = (step: FormState['step']) => {
  switch (step) {
    case 'adjustment_loss':
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
    case 'select_employees':
      return i18n.t('settings.timeoff_policies.add_employees_modal.assign.description')
  }
}

interface FormState {
  step: 'select_employees' | 'map_allowances' | 'adjustment_loss'
  allowancesToMap: AllowanceDataFragment[]
  mappings: BulkAssignEmployeesTimeoffPolicyMappingsInput[]
}

interface Props {
  loading: boolean
  timeoffPolicyId: number
  timeoffPolicyEmployeeIds: number[]
  timeoffPolicyAllowances: AllowanceDataFragment[]
  onClose: () => void
}

const AddEmployee = ({
  loading,
  timeoffPolicyId,
  timeoffPolicyEmployeeIds,
  timeoffPolicyAllowances,
  onClose,
}: Props) => {
  const [formState, setFormState] = useState<FormState>({
    step: 'select_employees',
    allowancesToMap: [],
    mappings: [],
  })
  const [employeeSelection, setemployeeSelection] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })

  const [fetchAllowancesWithIncidences] = useLazyQuery(FetchAllowancesWithIncidencesDocument)
  const [bulkAssignEmployees, { loading: loadingBulkAssign }] = useMutation(
    TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
    {
      extractorFn: (data) => data.timeoffMutations.bulkAssignEmployeesTimeoffPolicy,
    }
  )

  const employeeIds = employeeSelection.type === 'manual' ? employeeSelection.ids ?? [] : []
  const isLoading = loading || loadingBulkAssign

  const handleSelectEmployeesSubmit = async () => {
    const { data } = await fetchAllowancesWithIncidences({
      variables: {
        employeeIds,
      },
    })
    const allowancesToMap = uniqBy(
      compact(
        data?.employees.employees
          .flatMap((employee) => employee.timeoffAllowanceIncidences)
          .map((incidence) => incidence.timeoffAllowance)
      ),
      'id'
    )

    if (allowancesToMap.length > 0) {
      setFormState({
        ...formState,
        allowancesToMap,
        step: 'map_allowances',
      })
    } else {
      await bulkAssignEmployees({
        variables: {
          employeeSelection,
          mappings: [],
          policyId: timeoffPolicyId,
        },
        refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
      })
      onClose()
    }
  }

  const handleMapAllowancesSubmit = async () => {
    if (
      formState.mappings.length > 0 &&
      formState.mappings.every((mapping) => mapping.newAllowanceId !== 0)
    ) {
      await bulkAssignEmployees({
        variables: {
          employeeSelection,
          mappings: formState.mappings,
          policyId: timeoffPolicyId,
        },
        refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
      })
      onClose()
    } else {
      setFormState({
        ...formState,
        step: 'adjustment_loss',
      })
    }
  }

  const handleAdjustmentLossPrimary = () => {
    setFormState({
      ...formState,
      step: 'map_allowances',
    })
  }

  const handleAdjustmentLossSecondary = async () => {
    await bulkAssignEmployees({
      variables: {
        employeeSelection,
        mappings: formState.mappings,
        policyId: timeoffPolicyId,
      },
      refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
    })
    onClose()
  }

  const getPrimaryAction = () => {
    switch (formState.step) {
      case 'select_employees':
        return {
          label: i18n.t('settings.timeoff_policies.add_employees_modal.button'),
          onClick: handleSelectEmployeesSubmit,
          disabled:
            isLoading ||
            (employeeSelection.type === 'manual' && employeeSelection.ids?.length === 0),
          loading: isLoading,
        }
      case 'map_allowances':
        return {
          label: i18n.t('settings.timeoff_policies.add_employees_modal.adjustments.button'),
          onClick: handleMapAllowancesSubmit,
          disabled: isLoading,
          loading: isLoading,
        }
      case 'adjustment_loss':
        return {
          label: i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button'),
          onClick: handleAdjustmentLossPrimary,
          disabled: isLoading,
          loading: isLoading,
        }
    }
  }

  const getSecondaryAction = () => {
    if (formState.step === 'adjustment_loss') {
      return {
        label: i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button'),
        onClick: handleAdjustmentLossSecondary,
        disabled: isLoading,
        loading: isLoading,
      }
    }
    return undefined
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (isLoading) return
        onClose()
      }}
      title={getTitle(formState.step)}
      description={getDescription(formState.step)}
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          {formState.step === 'select_employees' && (
            <EmployeeSelectorV2
              messages={{
                placeholder: i18n.t('settings.timeoff_policies.add_employees_modal.assign.title'),
              }}
              forcedFilters={{
                excludeIds: timeoffPolicyEmployeeIds,
              }}
              filters={['team']}
              onChange={setemployeeSelection}
              options={{
                hideSearchIcon: true,
                hideLabel: true,
              }}
              multiple
            />
          )}
          {formState.step === 'map_allowances' && (
            <AllowanceMapper
              policyId={timeoffPolicyId}
              allowancesToMap={formState.allowancesToMap}
              timeoffPolicyAllowances={timeoffPolicyAllowances}
              onMappingsChange={(mappings) => setFormState({ ...formState, mappings })}
            />
          )}
        </>
      )}
    </F0Dialog>
  )
}

export default observer(AddEmployee)
